import type {
  RdOption,
  WorkspaceSummaryResponse,
  WorkspaceThreadSummary,
} from "../../../shared/api-types";
import { findRdBySegment } from "./recent-rds";

// The RD page's tabs in the single-rail frame (NO-754): which tabs an RD page
// shows, which one a URL is on, where switching RD lands, and which RD a URL
// names. Pure and relative-import only, so `tsx --test` runs it. The classic
// frame keeps its sidebar (rd-workspace-sidebar.tsx) and reads only the thread
// label from here. Chat is not a tab: an RD's chats live in the rail and the
// RD page's Chats card (rd-chats.ts), and a chat opens in a frame of its own.

export type RdTabId = "overview" | "metrics" | "sessions" | "meetings" | "profile";

/** Every surface a tab or sub-link opens; each takes the RD as `$rdId`. */
export type RdSurfacePath =
  | "/rds/$rdId"
  | "/rds/$rdId/metrics"
  | "/rds/$rdId/scorecards"
  | "/rds/$rdId/meetings"
  | "/rds/$rdId/forms"
  | "/rds/$rdId/observations";

export interface RdTabLink {
  to: RdSurfacePath;
  label: string;
  /** Absent while the counts are not this RD's (or not loaded). */
  count?: number;
}

export interface RdTab extends RdTabLink {
  id: RdTabId;
  /** Profile's second row: Functional forms, then Observations. */
  subLinks?: RdTabLink[];
}

export interface RdTabCounts {
  sessionCount: number;
  meetingNoteCount: number;
  formSnapshotCount: number;
  observationCount: number;
}

/**
 * The tabs in order. Overview and Metrics exist only while supervisor-metrics
 * is on, as the classic sidebar's Snapshot and Metrics rows do (NO-558).
 * Profile lands on Functional Forms, where the contract profile lives, with
 * Observations one sub-link away.
 */
export function rdPageTabs({
  metricsEnabled,
  counts,
}: {
  metricsEnabled: boolean;
  counts: RdTabCounts | null;
}): RdTab[] {
  const metricsTabs: RdTab[] = [
    { id: "overview", to: "/rds/$rdId", label: "Overview" },
    { id: "metrics", to: "/rds/$rdId/metrics", label: "Metrics" },
  ];
  const recordTabs: RdTab[] = [
    {
      id: "sessions",
      to: "/rds/$rdId/scorecards",
      label: "Sessions",
      count: counts?.sessionCount,
    },
    {
      id: "meetings",
      to: "/rds/$rdId/meetings",
      label: "Meetings",
      count: counts?.meetingNoteCount,
    },
    {
      id: "profile",
      to: "/rds/$rdId/forms",
      label: "Profile",
      subLinks: [
        {
          to: "/rds/$rdId/forms",
          label: "Functional forms",
          count: counts?.formSnapshotCount,
        },
        {
          to: "/rds/$rdId/observations",
          label: "Observations",
          count: counts?.observationCount,
        },
      ],
    },
  ];

  return metricsEnabled ? [...metricsTabs, ...recordTabs] : recordTabs;
}

/** The surface segment after `/rds/<rd>`: "" on the RD page itself, null off it. */
function surfaceSegment(pathname: string): string | null {
  const match = /^\/rds\/[^/]+(?:\/([^/]+))?/.exec(pathname);
  if (!match) return null;

  return match[1] ?? "";
}

const TAB_BY_SEGMENT = new Map<string, RdTabId>([
  ["", "overview"],
  ["metrics", "metrics"],
  ["scorecards", "sessions"],
  ["meetings", "meetings"],
  ["forms", "profile"],
  ["observations", "profile"],
]);

/** The tab a URL is on. A form snapshot and Observations are Profile; a chat is none. */
export function activeRdTab(pathname: string): RdTabId | null {
  const segment = surfaceSegment(pathname);
  if (segment === null) return null;

  return TAB_BY_SEGMENT.get(segment) ?? null;
}

export interface ActiveChat {
  /** On `/chats/new`. */
  newChat: boolean;
  /** The open thread's id, or null off a thread. */
  chatId: string | null;
}

/**
 * Which chat a URL is on, for the rail's chat rows. It reads the id from the
 * path, so a slug URL and an id URL mark the same thread.
 */
export function activeChat(pathname: string): ActiveChat {
  const match = /^\/rds\/[^/]+\/chats\/([^/]+)\/?$/.exec(pathname);
  const segment = match?.[1] ?? null;
  if (segment === "new") return { newChat: true, chatId: null };

  return { newChat: false, chatId: segment };
}

/** A chat (`/chats/new` or a thread) opens in the chat frame, without header or tabs. */
export function onRdChatPage(pathname: string): boolean {
  const chat = activeChat(pathname);

  return chat.newChat || chat.chatId !== null;
}

export function chatThreadLabel(thread: Pick<WorkspaceThreadSummary, "title">): string {
  return thread.title?.trim() || "Untitled chat";
}

/** Which Profile sub-link a URL is on, if any. */
export function activeProfileLink(pathname: string): RdSurfacePath | null {
  const segment = surfaceSegment(pathname);
  if (segment === "forms") return "/rds/$rdId/forms";

  if (segment === "observations") return "/rds/$rdId/observations";

  return null;
}

const SWITCH_PATH_BY_SEGMENT = new Map<string, RdSurfacePath>([
  ["", "/rds/$rdId"],
  ["metrics", "/rds/$rdId/metrics"],
  ["scorecards", "/rds/$rdId/scorecards"],
  ["meetings", "/rds/$rdId/meetings"],
  ["forms", "/rds/$rdId/forms"],
  ["observations", "/rds/$rdId/observations"],
]);

/**
 * Where switching to another RD lands: the same surface for that RD. A record
 * id (a form snapshot) belongs to the old RD, so it is dropped, and so is
 * every search param (meetingNoteId, edit, sourceType, sourceId), because the
 * caller navigates with the path alone. A chat has no switcher; were one ever
 * asked from a chat URL, it lands on the RD page.
 */
export function switchTarget(
  pathname: string,
  rd: Pick<RdOption, "slug">
): { to: RdSurfacePath; params: { rdId: string } } {
  const segment = surfaceSegment(pathname);
  const to = segment === null ? undefined : SWITCH_PATH_BY_SEGMENT.get(segment);

  return { to: to ?? "/rds/$rdId", params: { rdId: rd.slug } };
}

/** The RD id a slug-or-id URL param names; the raw param until the roster says. */
export function canonicalRdId(param: string, options: readonly RdOption[]): string {
  return findRdBySegment(param, options)?.id ?? param;
}

export interface RdOutletKey {
  param: string;
  key: string;
}

/**
 * The key the frame remounts the surface on. It changes when the URL names a
 * different RD, holds when the roster merely finishes loading (so a page that
 * opened before rd-options answered is not torn down a second later), and
 * holds when the same RD is reached by slug after id or id after slug.
 */
export function nextOutletKey(
  previous: RdOutletKey | null,
  param: string,
  options: readonly RdOption[]
): RdOutletKey {
  if (previous?.param === param) return previous;

  return { param, key: canonicalRdId(param, options) };
}

/**
 * The workspace summary only when it is this RD's. The summary query keeps the
 * previous RD's answer as placeholder data while the new one loads, and the
 * frame must not show that RD's counts or threads under this RD's header.
 */
export function summaryForRd(
  summary: WorkspaceSummaryResponse | undefined,
  param: string,
  canonicalId: string
): WorkspaceSummaryResponse | null {
  if (!summary) return null;

  if (summary.rdId === canonicalId) return summary;

  return summary.rdId === param || summary.rdSlug === param ? summary : null;
}

export function summaryTabCounts(
  summary: WorkspaceSummaryResponse | null
): RdTabCounts | null {
  if (!summary) return null;

  return {
    sessionCount: summary.sessionCount,
    meetingNoteCount: summary.meetingNoteCount,
    formSnapshotCount: summary.formSnapshotCount,
    observationCount: summary.observationCount,
  };
}
